import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowUp } from "react-icons/fi";
import { startNewConversation } from "../../store/aiChatSlice";
import { AI_ANALYST_PATH, type AnalystSuggestion } from "../../utils/operationsDashboard";

interface AnalystInsightWidgetProps {
  /** Starter questions built from what the board is showing — see analystSuggestions. */
  suggestions: AnalystSuggestion[];
  className?: string;
}

// The page's one dark panel: it only asks, handing questions to the real chat rather than being a second chat to keep in sync
const AnalystInsightWidget: React.FC<AnalystInsightWidgetProps> = ({
  suggestions,
  className,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [draft, setDraft] = useState("");

  // The page owns the conversation, so the question travels as navigation state and sends on arrival
  const ask = (question: string) => {
    const text = question.trim();
    if (!text) return;
    // Clear the thread BEFORE navigating, or the analyst page's URL sync restores the old conversation
    dispatch(startNewConversation());
    navigate(AI_ANALYST_PATH, { state: { initialInput: text, autoSend: true } });
  };

  return (
    <section
      className={`flex min-w-0 flex-col gap-3 rounded-eai-card bg-eai-surface-dark p-4 shadow-eai-card ${className}`}
    >
      <div className="flex shrink-0 items-center justify-between gap-3">
        {/* The page eyebrow exactly, in the one colour the dark ground leaves legible */}
        <p className="eai-eyebrow text-eai-accent-grn">
          {"//"} AI Analyst
        </p>
        <Link
          to={AI_ANALYST_PATH}
          className="shrink-0 font-outfit text-eai-body-xs font-medium text-eai-teal-mist transition-colors hover:text-white"
        >
          Open
        </Link>
      </div>

      {/* No headline: the placeholder already invites the question, and a paragraph saying so cost half the panel */}
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            ask(draft);
          }}
          // Bordered but unfilled, like the chips under it — the dark ground has no soft-surface token to sit on
          className="flex items-center gap-2 rounded-full border border-eai-lt-dark px-3 py-1.5 transition-colors focus-within:border-eai-cta-primary"
        >
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Ask the analyst…"
            aria-label="Ask the AI Analyst"
            className="min-w-0 flex-1 bg-transparent font-inter text-eai-body-sm text-eai-secondary outline-none placeholder:text-eai-teal-deep"
          />
          <button
            type="submit"
            disabled={!draft.trim()}
            aria-label="Send to AI Analyst"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-eai-cta-primary text-white transition-colors hover:bg-eai-cta-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FiArrowUp size={13} />
          </button>
        </form>

        {suggestions.length > 0 && (
          // Built from live signals; scrolls rather than sizing the panel, since the chips are the one part with no fixed count
          <div className="scrollable-div flex min-h-0 flex-1 flex-wrap content-start gap-1.5 overflow-y-auto">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                type="button"
                onClick={() => ask(suggestion.question)}
                title={suggestion.question}
                className="max-w-full truncate rounded-full border border-eai-lt-dark px-3 py-1 font-outfit text-eai-body-xs text-eai-teal-mist transition-colors hover:border-eai-cta-primary hover:text-white active:border-eai-cta-primary"
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AnalystInsightWidget;
